import React, { useState } from 'react';
import { CloudUploadIcon } from '../../assets/icons/CloudUploadIcon';
import { FileTypes, ImageTypes } from '../../libraries/FileTypes';
import { getMergedClassNames, getUniqueKey } from '../../utils/HTMLutils';
import './FileUpload.scss';
const rootClassName = 'component-file-upload';
const DRAG_ENTER_CLASS_NAME = 'drag-enter';
const DRAG_ENTER_DISABLED_CLASS_NAME = 'drag-disabled';

interface FileUploadProps {
  label: string | React.ReactNode;
  topLabel?: string | React.ReactNode;
  helperText?: string | React.ReactNode;
  hideTopLabel?: boolean;
  hideHelperText?: boolean;
  error?: boolean;
  onChange?: (evt?: any) => void;
  inputFileProps?: object;
  allowedFileTypes?: FileTypes[];
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
}

export default function FileUpload(props: FileUploadProps) {
  const [id] = useState(getUniqueKey());

  const addClass = (className: string) => {
    const el = document.querySelector(
      `#${id} .${rootClassName}-droppable`
    ) as HTMLElement;
    if (el) {
      el.classList.add(className);
    }
  };
  const removeClass = (className: string) => {
    const el = document.querySelector(
      `#${id} .${rootClassName}-droppable`
    ) as HTMLElement;
    if (el) {
      el.classList.remove(className);
    }
  };

  const handleDrop = (event: any) => {
    const files = event.dataTransfer.files;
    const filteredFiles = Array.from(files).filter(
      (file: any) =>
        !props.allowedFileTypes ||
        !props.allowedFileTypes.length ||
        isAllowedFileType(file.name)
    );
    if (props.multiple) {
      props.onChange?.(filteredFiles);
    } else if (filteredFiles.length) {
      props.onChange?.(filteredFiles[0]);
    }
    const hiddenInput = document.querySelector(
      `#${id} input`
    ) as HTMLInputElement;
    if (hiddenInput) {
      hiddenInput.value = '';
    }
  };

  const handleClick = () => {
    const input = document.querySelector(`#${id} input`) as HTMLInputElement;
    if (!props.disabled && input) {
      input.click();
    }
  };

  const isAllowedFileType = (fileName: string) => {
    const extension = String(fileName)
      .replace(/^(.)*(\..+)$/, '$2')
      .toLowerCase();
    if (props.allowedFileTypes && props.allowedFileTypes.length) {
      if (props.allowedFileTypes.indexOf(extension as FileTypes) > -1) {
        return true;
      } else if (
        props.allowedFileTypes.indexOf(FileTypes.ALL_IMAGES) > -1 &&
        Object.values(ImageTypes).indexOf(extension as ImageTypes) > -1
      ) {
        return true;
      } else {
        return false;
      }
    }
    return true;
  };

  return (
    <div className={`${rootClassName}-outer`}>
      {!props.hideTopLabel && (
        <div
          className={getMergedClassNames([
            `${rootClassName}-top-label`,
            props.disabled ? 'disabled' : '',
            props.error ? 'error' : '',
            'nunito',
          ])}
        >
          {props.topLabel}
        </div>
      )}
      <div
        id={id}
        className={getMergedClassNames([
          rootClassName,
          props.disabled ? 'disabled' : '',
          props.error ? 'error' : '',
        ])}
      >
        <div
          onClick={handleClick}
          className={getMergedClassNames([
            `${rootClassName}-droppable`,
            props.disabled ? 'disabled' : '',
            props.error ? 'error' : '',
          ])}
          onDragEnter={(event) => {
            event.preventDefault();
            event.stopPropagation();
            addClass(
              props.disabled
                ? DRAG_ENTER_DISABLED_CLASS_NAME
                : DRAG_ENTER_CLASS_NAME
            );
          }}
          onDrop={(event) => {
            event.preventDefault();
            event.stopPropagation();
            removeClass(DRAG_ENTER_CLASS_NAME);
            removeClass(DRAG_ENTER_DISABLED_CLASS_NAME);
            if (props.disabled) return;
            handleDrop(event);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            event.stopPropagation();
            removeClass(DRAG_ENTER_CLASS_NAME);
            removeClass(DRAG_ENTER_DISABLED_CLASS_NAME);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            event.stopPropagation();
            addClass(
              props.disabled
                ? DRAG_ENTER_DISABLED_CLASS_NAME
                : DRAG_ENTER_CLASS_NAME
            );
          }}
        />
        <div className={`${rootClassName}-row`}>
          <div className={`${rootClassName}-upload-icon`}>
            <CloudUploadIcon />
          </div>
          <div className={`${rootClassName}-label`}>{props.label}</div>
        </div>
        <input
          type='file'
          accept={
            props.allowedFileTypes
              ? Array(props.allowedFileTypes).join(', ')
              : undefined
          }
          {...props.inputFileProps}
          onChange={(evt) => {
            const files: any = evt.target.files;
            const filteredFiles = Array.from(files).filter(
              (file: any) =>
                !props.allowedFileTypes ||
                !props.allowedFileTypes.length ||
                isAllowedFileType(file.name)
            );
            if (props.multiple) {
              props.onChange?.(filteredFiles);
            } else {
              props.onChange?.(filteredFiles[0]);
            }
            const hiddenInput = document.querySelector(
              `#${id} input`
            ) as HTMLInputElement;
            if (hiddenInput) {
              hiddenInput.value = '';
            }
          }}
          multiple={props.multiple}
          disabled={props.disabled}
        />
      </div>
      {!props.hideHelperText && (
        <div
          className={getMergedClassNames([
            `${rootClassName}-helper-text`,
            props.disabled ? 'disabled' : '',
            props.error ? 'error' : '',
          ])}
        >
          {props.helperText}
        </div>
      )}
    </div>
  );
}

FileUpload.defaultProps = {
  label: 'Arraste e solte o arquivo nesta área ou procure no dispositivo.',
  allowedFileTypes: [],
  multiple: false,
  disabled: false,
  hideTopLabel: false,
  hideHelperText: false,
  error: false,
};
